<script setup>
import {useNotification} from '@kyvg/vue3-notification'
import {storeToRefs} from 'pinia'
import {ref, defineEmits, onMounted, defineProps, computed, nextTick} from 'vue'
import TheSelect from '@shared-components/ui/selects/TheSelect.vue'
import {Modal as BaseModal} from 'bootstrap'
import BaseForm from '@shared-components/base/BaseForm.vue'
import {useAuthenticationStore} from '@/stores'
import {useCompanyStore} from '@/views/companies/CompanyStore'
import {useCompanyBillingStore} from '@/views/finance/CompanyBillingStore'
import ApiErrors from '@shared-components/ui/ApiErrors.vue'
import dayjs from 'dayjs'
import {
  getCompanyInvoicePdf,
  getCurrencySign,
  hideElementIfExist,
  openUrl,
  showElementIfExist,
} from '@shared/utils/helpers'
import copyToClipboard from '@shared/utils/dom/copyToClipboard'
import {pickDefaultLegalEntity} from '@shared/utils/quickBooksCustomers'
import TheSelectDate from '@shared-components/ui/selects/TheSelectDate.vue'
import {defaultPaymentTypeOptions, getPaymentTypeOptions} from '@/views/finance/PaymentRepeatables'
import TheSwitcher from '@shared-components/ui/inputs/TheSwitcher.vue'
import PaymentMethodList from '@shared-components/finance/proforma-invoice/PaymentMethodList.vue'
import ProformaInvoiceSuccess from '@shared-components/finance/proforma-invoice/ProformaInvoiceSuccess.vue'
import InvoiceServiceSection from '@/views/finance/components/invoice-modal-parts/InvoiceServiceSection.vue'
import InvoicePaymentsSection from '@/views/finance/components/invoice-modal-parts/invoice-payments-section/InvoicePaymentsSection.vue'
import {errorNotify} from '@/services/notify/helpers'

const emit = defineEmits(['created', 'hidden', 'updated'])
const props = defineProps({
  id: {
    type: String,
    default: 'invoice-modal',
  },
  parentModalId: {
    type: String,
    default: '',
  },
  invoiceToEdit: {
    type: Object,
    default: null,
  },
  invoiceType: {
    type: [String, undefined],
    default: 'estimate',
  },
  companyHash: {
    type: String,
    default: null,
  },
})

const {isCoreTeam, userCompanyHash, user} = storeToRefs(useAuthenticationStore())
const {getAuthUserFullInfo} = useAuthenticationStore()
const {companiesOptions} = storeToRefs(useCompanyStore())
const {getCompanyByHash} = useCompanyStore()
const {notify} = useNotification()
const {createInvoice, updateInvoice, getInvoiceInfo, getQuickBooksCustomers, getInvoiceRequisites, sendInvoiceEmail} =
  useCompanyBillingStore()

// Refs
const invoiceServices = ref()
const invoicePayments = ref()
const modal = ref()
const loading = ref(false)
const isInvoiceSent = ref(false)
const activeCompany = ref(null)
const error = ref(null)
const invoiceUrl = ref(null)
const createdInvoice = ref(null)
const quickBooksCustomersOptions = ref([])
const quickBooksCustomers = ref([])
const hasPeriodDate = ref(false)
const clientInfo = ref(null)
const requisites = ref('')
const requisitesTag = ref(null)
const requisitesLoading = ref(false)
const defaultFormData = {
  company_hash: props.companyHash ?? userCompanyHash.value,
  quick_books_customer_id: null,
  payment_type: undefined,
  invoice_date: dayjs().format('YYYY-MM-DD'),
  due_date: dayjs().add(1, 'day').format('YYYY-MM-DD'),
  send_email: false,
  send_to_email: '',
  period_from: undefined,
  period_to: undefined,
  send_to_managers: '',
  line_items: [],
  payments: [],
  type: props.invoiceType,
}

const defaultOptionalFormData = {
  period_from: dayjs().format('YYYY-MM-DD'),
  period_to: dayjs().add(1, 'day').format('YYYY-MM-DD'),
}
const optionalFormData = ref(cloneSafe(defaultOptionalFormData))
const formData = ref(cloneSafe(defaultFormData))
const currency = ref(getCurrencySign('USD'))
const isSendToManagers = ref(!!formData.value.send_to_managers)

// Computed
const selectedCustomer = computed(() =>
  quickBooksCustomersOptions.value.find(item => item?.value === formData.value.quick_books_customer_id),
)

const totalPrice = computed(() => {
  return formData.value.line_items.reduce((sum, item) => {
    return sum + (Number(item.price) || 0)
  }, 0)
})

const hasPaymentStep = computed(() => props.invoiceType !== 'invoice')
const isInvoiceCreated = computed(() => !!createdInvoice.value)
const successTitle = computed(() => {
  return props.invoiceType === 'invoice' ? 'Invoice successfully generated' : 'Proforma invoice successfully generated'
})

const formError = computed(() => invoiceServices.value?.validationError || error.value)
const isServicesValid = computed(() => {
  return invoiceServices.value?.isServicesValid
})
// Methods
onMounted(() => {
  const modalElement = document.getElementById(props.id)
  const modalBackdrop = props.parentModalId ? 'static' : true

  modal.value = new BaseModal(modalElement, {backdrop: modalBackdrop})

  modalElement.addEventListener('hidden.bs.modal', () => {
    emit('hidden')
  })

  init()
})

const init = async () => {
  if (props.invoiceToEdit) {
    try {
      let invoice = await getInvoiceInfo(props.invoiceToEdit.id, {
        with: ['company', 'line_items'],
        type: props.invoiceType,
      })

      let companyHash = invoice.company.hash
      delete invoice.company

      formData.value = invoice
      formData.value.company_hash = companyHash

      await onCompanyChanged(formData.value.company_hash)
    } catch (error) {
      return notify({type: 'error', text: error})
    }
  } else if (formData.value.company_hash) {
    if (props.companyHash === null) {
      clientInfo.value = await getAuthUserFullInfo()
      activeCompany.value = cloneSafe(clientInfo?.value?.company)
    }
    await onCompanyChanged(formData.value.company_hash)
    await invoiceServices?.value?.initLineItems()
  } else {
    await invoiceServices?.value?.initLineItems()
  }

  handleOpenModal()
}

const handleSubmitInvoice = async () => {
  if (props.invoiceToEdit) {
    await handleEdit()

    return
  }

  formData.value.send_email = !hasPaymentStep.value

  const invoice = await handleCreate()

  if (invoice && !hasPaymentStep.value && !invoice.invoice_url) {
    handleCloseModal()
  }
}

const handleDownloadPdf = () => {
  getCompanyInvoicePdf(createdInvoice.value.hash)
}

const sendInvoiceTo = async recipients => {
  error.value = null
  loading.value = true

  try {
    await sendInvoiceEmail(createdInvoice.value.hash, recipients)
    isInvoiceSent.value = true
  } catch (responseError) {
    error.value = responseError?.data
  } finally {
    loading.value = false
  }
}

const handleSendEmail = async () => {
  await sendInvoiceTo(formData.value.send_to_email)
}

const handleSendManagers = async () => {
  await sendInvoiceTo(formData.value.send_to_managers)
}

const hideParentModal = () => {
  hideElementIfExist(props.parentModalId)
}

const showParentModal = () => {
  showElementIfExist(props.parentModalId)
}

const handleOpenModal = () => {
  props.parentModalId ? hideParentModal() : ''

  openModal()
}
const handleCloseModal = () => {
  props.parentModalId ? showParentModal() : ''

  closeModal()
}

const openModal = () => {
  modal.value.show()
}

const closeModal = () => {
  modal.value.hide()
}
const handleCreate = async () => {
  loading.value = true

  try {
    let invoice = await createInvoice(getRequestParams())
    emit('created', invoice)

    invoiceUrl.value = invoice.invoice_url
    createdInvoice.value = invoice
    error.value = null

    return invoice
  } catch (responseError) {
    error.value = responseError?.data

    return null
  } finally {
    loading.value = false
  }
}

const handleEdit = async () => {
  loading.value = true

  try {
    await updateInvoice(getRequestParams())
  } catch (responseError) {
    return (error.value = responseError?.data)
  } finally {
    loading.value = false
  }

  emit('updated')
  handleCloseModal()
  notify({type: 'success', text: 'Invoice updated'})
}

const getRequestParams = () => {
  let params

  params = cloneSafe(formData.value)

  if (hasPeriodDate.value) {
    params.period_from = optionalFormData.value.period_from
    params.period_to = optionalFormData.value.period_to
  } else {
    params.period_from = null
    params.period_to = null
  }

  params.line_items.forEach(item => {
    let ioText = ''
    delete item._isNew

    if (item.service_id === invoiceServices?.value?.productIds.advertisingServices) {
      ioText = selectedCustomer.value?.io ? `Advertising Agreement No ${selectedCustomer.value.io}.\n` : ''
    }

    const periodText =
      hasPeriodDate.value && optionalFormData.value.period_from && optionalFormData.value.period_to
        ? `Period - ${dayjs(optionalFormData.value.period_from).format('YYYY/MM/DD')} - ${dayjs(
            optionalFormData.value.period_to,
          ).format('YYYY/MM/DD')}`
        : ''

    item.service_description = item.service_description.trim() + '\n' + ioText + periodText
  })

  if (Array.isArray(params.payments) && params.payments.length > 0) {
    let remaining = totalPrice.value

    params.payments = params.payments
      .map(item => {
        const unapplied = Number(item.unapplied_amount) || 0

        if (remaining <= 0) {
          return null
        }

        const appliedAmount = Math.min(unapplied, remaining)
        remaining -= appliedAmount

        return {
          payment_id: +item.id,
          amount: Number(appliedAmount.toFixed(2)),
        }
      })
      .filter(Boolean)
  }

  if (!isSendToManagers.value) {
    params.send_to_managers = undefined
  }

  params.with = ['company']
  params.type = props.invoiceType
  params.currency = defaultPaymentTypeOptions.find(item => item.value === formData.value.payment_type)?.currency

  return params
}

const resetPaymentType = () => {
  formData.value.payment_type = undefined
  requisites.value = ''
  requisitesTag.value = null
}

const refreshQuickBooksPayments = async () => {
  if (props.invoiceType !== 'invoice') {
    return
  }

  await nextTick()
  await invoicePayments?.value?.fetchQuickBooksPayments()
}

const setCurrency = () => {
  const paymentType = defaultPaymentTypeOptions.find(item => item.value === formData.value.payment_type)

  if (paymentType) {
    currency.value = getCurrencySign(paymentType.currency)
  }
}

const onPaymentTypeChange = async () => {
  setCurrency()

  requisitesLoading.value = true

  try {
    const paymentRequisites = await getInvoiceRequisites(formData.value.payment_type)
    requisites.value = paymentRequisites.requisites
    requisitesTag.value = paymentRequisites.tag
  } catch (error) {
    requisites.value = ''
    requisitesTag.value = null
    errorNotify(notify, error)
  } finally {
    requisitesLoading.value = false
  }
}

const onCompanyChanged = async companyHash => {
  resetPaymentType()

  invoicePayments?.value?.resetQbPayments()
  formData.value.payments = []

  if (!companyHash) {
    return (quickBooksCustomersOptions.value = [])
  }

  if (isCoreTeam.value) {
    await handleGetCompany()
  }

  const customers = await getQuickBooksCustomers(companyHash)

  quickBooksCustomers.value = cloneSafe(customers)
  quickBooksCustomersOptions.value = customers.map(({id, company_name, email, io, io_company}) => ({
    value: id,
    label: company_name,
    email,
    io,
    io_company,
  }))

  const defaultCustomer = pickDefaultLegalEntity(customers)

  formData.value.quick_books_customer_id = defaultCustomer?.id ?? null
  formData.value.send_to_email = getInvoiceRecipientEmail(defaultCustomer)

  setSendToManagersField()

  if (!formData.value.send_to_managers) {
    isSendToManagers.value = false
  }
  if (!formData.value.send_to_email) {
    formData.value.send_email = false
  }

  await refreshQuickBooksPayments()
}

const getInvoiceRecipientEmail = entity => {
  return entity?.email || user.value?.email || ''
}

const setSendToManagersField = () => {
  if (!activeCompany.value) {
    return
  }

  const managerRoles = ['csm', 'csm_2', 'media_planner', 'media_planner_2']

  const emails = managerRoles.map(role => activeCompany.value[role]?.email).filter(email => !!email)

  const uniqueEmails = [...new Set(emails)]

  formData.value.send_to_managers = uniqueEmails.join(', ')
}
const handleGetCompany = async () => {
  loading.value = true

  try {
    activeCompany.value = await getCompanyByHash(formData.value.company_hash)
  } catch (error) {
    notify({type: 'error', text: error})
  } finally {
    loading.value = false
  }
}

const setDueDate = () => {
  const invoiceDate = formData.value.invoice_date
  const daysToAdd = Number(activeCompany.value?.net_terms) || 0

  const parsedDate = dayjs(invoiceDate)

  formData.value.due_date = parsedDate.add(daysToAdd, 'day').format('YYYY-MM-DD')
}
const updateInvoiceDateRange = () => {
  formData.value.invoice_date = optionalFormData.value.period_to
  setDueDate()
}
const onEntityChange = async () => {
  const currentEntity = quickBooksCustomers.value.find(item => item.id === formData.value.quick_books_customer_id)

  formData.value.send_to_email = getInvoiceRecipientEmail(currentEntity)
  resetPaymentType()

  await refreshQuickBooksPayments()
}
</script>

<template>
  <div class="modal fade" :id="props.id" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog max-w-1000-px">
      <div class="modal-content">
        <div v-if="!isInvoiceCreated" class="modal-header custom px-7 py-5">
          <h5 class="modal-title mb-0 custom fs-6">
            <template v-if="!props.invoiceToEdit">
              {{ props.invoiceType === 'invoice' ? 'New Invoice' : 'New Proforma Invoice' }}
            </template>
            <template v-else>
              Edit invoice #<b>{{ formData.id }}</b>
            </template>
          </h5>
          <button type="button" class="btn-close" aria-label="Close" @click="handleCloseModal" />
        </div>
        <button
          v-else
          type="button"
          class="btn-close proforma-close custom p-7"
          aria-label="Close"
          @click="handleCloseModal"
        />
        <base-form @on-submitted="handleSubmitInvoice">
          <div class="modal-body custom p-7">
            <div v-if="!isInvoiceCreated">
              <div class="d-flex custom gap-7 mb-7">
                <div v-if="isCoreTeam && props.companyHash === null" class="flex-grow-1">
                  <label class="custom fs-5 mb-4">Company</label>
                  <the-select
                    v-model="formData.company_hash"
                    :options="companiesOptions"
                    @change="onCompanyChanged"
                    :disabled="loading || isInvoiceCreated"
                    :allow_clear="false"
                    placeholder="Select Company"
                    required
                  />
                </div>
                <div class="flex-grow-1">
                  <label class="custom fs-5 mb-4">Legal Entity</label>
                  <the-select
                    v-model="formData.quick_books_customer_id"
                    :options="quickBooksCustomersOptions"
                    :disabled="loading || isInvoiceCreated"
                    :allow_clear="false"
                    @change="onEntityChange"
                    id="invoice-quick_books_customer_id"
                    placeholder="Select Legal Entity"
                    required
                  />
                </div>
              </div>

              <template v-if="isCoreTeam">
                <div class="form-group custom mb-5">
                  <the-switcher
                    v-model="hasPeriodDate"
                    label="Set period"
                    @change="updateInvoiceDateRange"
                    :disabled="loading || isInvoiceCreated"
                    id="has-period-date"
                  />
                </div>
                <div class="d-flex custom gap-4 mb-7">
                  <template v-if="hasPeriodDate">
                    <div class="flex-grow-1">
                      <label class="custom fs-5 mb-4">From</label>
                      <the-select-date
                        v-model="optionalFormData.period_from"
                        :loading="loading"
                        :disabled="loading || isInvoiceCreated"
                        required
                      />
                    </div>
                    <div class="flex-grow-1">
                      <label class="custom fs-5 mb-4">To</label>
                      <the-select-date
                        v-model="optionalFormData.period_to"
                        :loading="loading"
                        @updated="updateInvoiceDateRange"
                        :disabled="loading || isInvoiceCreated"
                        required
                      />
                    </div>
                  </template>
                  <div class="flex-grow-1">
                    <label class="custom fs-5 mb-4" for="invoice-date">Invoice Date</label>
                    <the-select-date
                      v-model="formData.invoice_date"
                      :loading="loading"
                      :disabled="hasPeriodDate || loading || isInvoiceCreated"
                      @updated="setDueDate"
                      required
                    />
                  </div>
                  <div class="flex-grow-1">
                    <label class="custom fs-5 mb-4" for="invoice-due_date">Due Date</label>
                    <the-select-date
                      v-model="formData.due_date"
                      :loading="loading"
                      :disabled="hasPeriodDate || loading || isInvoiceCreated"
                      required
                    />
                  </div>
                </div>
              </template>

              <invoice-service-section
                v-model="formData.line_items"
                ref="invoiceServices"
                :recommended-topup-amount="activeCompany?.balance_info?.recommended_top_up_amount"
                :disabled="loading || isInvoiceCreated"
                :invoice-type="invoiceType"
                :currency="currency"
              />

              <invoice-payments-section
                v-if="
                  !invoiceToEdit && isCoreTeam && props.invoiceType === 'invoice' && formData.quick_books_customer_id
                "
                v-model="formData.payments"
                :quick-book-customer-id="formData.quick_books_customer_id"
                :company-hash="formData.company_hash"
                :total-price="totalPrice"
                :disabled="loading || isInvoiceCreated"
                class="custom mt-7"
                ref="invoicePayments"
              />

              <template v-if="hasPaymentStep">
                <h5 class="proforma-title custom fs-5 mt-7 mb-5">Payment Methods</h5>
                <payment-method-list
                  v-model="formData.payment_type"
                  :options="getPaymentTypeOptions(selectedCustomer?.io_company)"
                  :disabled="loading || !formData.quick_books_customer_id"
                  @change="onPaymentTypeChange"
                />
              </template>

              <div v-if="invoiceUrl" class="alert alert-light custom mt-5" role="alert">
                <label class="custom fs-5 mb-4">Invoice url</label>
                <div class="input-group">
                  <input class="form-control" id="invoice-link" :value="invoiceUrl" readonly />
                  <div class="input-group-text">
                    <span
                      class="copy-btn clickable-icon"
                      @click="copyToClipboard"
                      data-clipboard-target="#invoice-link"
                      v-tooltip="'Copy to clipboard'"
                    >
                      <i class="fa-regular fa-clipboard" />
                    </span>
                    <span
                      class="open-in-new-tab-btn clickable-icon ml-3"
                      @click="openUrl(invoiceUrl)"
                      v-tooltip="'Open in a new tab'"
                    >
                      <i class="fa-solid fa-link" />
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <proforma-invoice-success
              v-else-if="hasPaymentStep"
              v-model:email="formData.send_to_email"
              v-model:managers="formData.send_to_managers"
              :title="successTitle"
              :payment-type="formData.payment_type"
              :requisites="requisites"
              :requisites-tag="requisitesTag"
              :requisites-loading="requisitesLoading"
              :disabled="loading"
              :email-sent="isInvoiceSent"
              @download="handleDownloadPdf"
              @send-email="handleSendEmail"
              @send-managers="handleSendManagers"
            />

            <api-errors v-if="formError" :error="formError" class="mt-3" />
          </div>
          <div v-if="!isInvoiceCreated" class="modal-footer justify-content-end custom gap-4 px-7 py-4">
            <button type="button" class="btn btn-secondary" @click="handleCloseModal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="loading || !isServicesValid">
              <template v-if="invoiceToEdit">Save</template>
              <template v-else>Generate</template>
            </button>
          </div>
        </base-form>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.proforma-close {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
}

.proforma-title {
  font-weight: 600;
  line-height: 1.4;
  color: $black;
}
</style>
